import React, {
  useState,
  ChangeEvent,
  FormEvent,
  useEffect,
} from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import { ME } from "../graphql/query";
import { UPDATE_PROFILE } from "../graphql/mutation";
import { MeQueryResponse } from "../interfaces/auth.interface";
import { BUTTON_TEXTS } from "../utils/const";
import {
  Box,
  TextField,
  Button,
  useMediaQuery,
  Modal,
  Autocomplete,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { countries, getEmojiFlag, TCountryCode } from "countries-list";
import * as Flags from "country-flag-icons/react/3x2";
import {
  ContactFormData,
  ContactInfoFormProps,
  UpdateProfilePayload,
} from "../interfaces/contactForm";
import { MSG } from "../utils/const";
import themestyle from '../theme';
import { INPUT_REGEX } from "../utils/const";
import { MESSAGES } from "../utils/const";
import { FORM_LABELS, FORM_PLACEHOLDERS } from "../utils/const";

import {
  getPhoneValidationMessage as validatePhoneNumber,
  PHONE_INPUT_MAX_DIGITS,
} from "../utils/phoneValidation";

const DEFAULT_COUNTRY = "India";

const getCountryDialCode = (countryName: string) => {
  const matchedCountry = Object.entries(countries).find(
    ([, country]) => country.name === countryName
  );

  return matchedCountry?.[1]?.phone?.[0] || "";
};

// Phone here is required; libphonenumber-js (via the shared util) does the accurate
// per-country length/format check once the field is non-empty.
const getPhoneValidationMessage = (phone: string, countryName: string) => {
  if (!phone.replace(/\D/g, "")) return MSG.PHONE_REQUIRED;
  return validatePhoneNumber(phone, countryName);
};

const AdminOnboardModel: React.FC<ContactInfoFormProps> = ({ onSubmit }) => {
  const { data } = useQuery<MeQueryResponse>(ME);
  const [updateProfile, { loading }] = useMutation(UPDATE_PROFILE, {
    refetchQueries: [{ query: ME }],
  });
  const isMobile = useMediaQuery("(max-width:480px)");
  const [errors, setErrors] = useState({
    name: "",
    country: "",
    phone: "",
    postcode: "",
    address: "",
  });

  const [formData, setFormData] = useState<ContactFormData>({
    email: "",
    name: "",
    phone: "",
    address: "",
    country: DEFAULT_COUNTRY,
    postcode: "",
  });
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_COUNTRY);
  const [countryMenuAnchor, setCountryMenuAnchor] = useState<null | HTMLElement>(null);
  const [countrySearch, setCountrySearch] = useState("");

  useEffect(() => {
    if (data?.me?.email) {
      setFormData((prev) => ({ ...prev, email: data.me.email }));
    }
  }, [data]);

  const countryOptions = Object.entries(countries)
    .map(([code, country]) => ({
      code: code as TCountryCode,
      name: country.name,
      emoji: getEmojiFlag(code as TCountryCode),
      dialCode: String(country.phone?.[0] || ""),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "name" || name === "country") {
      setFormData((prev) => ({
        ...prev,
        [name]: value.replace(INPUT_REGEX.ONLY_ALPHABETS_SPACE, ""),
      }));
      return;
    }
    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "");
      setFormData((prev) => ({ ...prev, phone: digitsOnly }));
      setErrors((prev) => ({
        ...prev,
        phone: digitsOnly ? getPhoneValidationMessage(digitsOnly, phoneCountry) : "",
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (event: any, newValue: { code: string; name: string } | null) => {
    const nextCountry = newValue ? newValue.name : "";
    setFormData((prev) => ({
      ...prev,
      country: nextCountry,
    }));
    setErrors((prev) => ({
      ...prev,
      country: newValue ? "" : MSG.COUNTRY_REQUIRED,
    }));
  };

  const handlePhoneCountryChange = (newValue: { code: string; name: string } | null) => {
    const nextCountry = newValue?.name || DEFAULT_COUNTRY;
    setPhoneCountry(nextCountry);
    setErrors((prev) => ({
      ...prev,
      phone: formData.phone ? getPhoneValidationMessage(formData.phone, nextCountry) : "",
    }));
  };

  const handleCountryMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCountryMenuAnchor(event.currentTarget);
  };

  const handleCountryMenuClose = () => {
    setCountryMenuAnchor(null);
    setCountrySearch("");
  };

  const filteredCountryOptions = countryOptions.filter((country) => {
    const query = countrySearch.trim().toLowerCase();
    if (!query) return true;

    return (
      country.name.toLowerCase().includes(query) ||
      country.dialCode.includes(query) ||
      country.code.toLowerCase().includes(query)
    );
  });

  const selectedCountry =
    countryOptions.find((country) => country.name === formData.country) ||
    countryOptions.find((country) => country.name === DEFAULT_COUNTRY) ||
    null;

  const selectedPhoneCountry =
    countryOptions.find((country) => country.name === phoneCountry) ||
    countryOptions.find((country) => country.name === DEFAULT_COUNTRY) ||
    null;

  const selectedDialCode = getCountryDialCode(phoneCountry || DEFAULT_COUNTRY);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let msg = "";
    switch (name) {
      case "phone":
        msg = getPhoneValidationMessage(value, phoneCountry);
        break;
      case "postcode":
        if (!value) msg = MSG.POSTCODE_REQUIRED;
        else if (!MSG.POSTCODEREG.test(value)) msg = MSG.POSTCODE_INVALID;
        break;
      case "address":
        if (!value) msg = MSG.ADDRESS_REQUIRED;
        else if (value.length < 5) msg = MSG.ADDRESS_MIN;
        break;
      case "name":
        if (!value) msg = MSG.FULLNAME_REQUIRED;
        break;
      case "country":
        if (!value) msg = MSG.COUNTRY_REQUIRED;
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: msg }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload: UpdateProfilePayload = {
      fullname: formData.name,
      phoneNumber: formData.phone.replace(/\D/g, ""),
      address: formData.address,
      country: formData.country,
      postcode: formData.postcode,
    };
    try {
      const { data: response } = await updateProfile({ variables: { input: payload } });
      const result = response?.updateProfile;
      if (!result?.success) {
        const message = result?.message || "Failed to update profile";
        if (/phone number already exists/i.test(message)) {
          setErrors((prev) => ({ ...prev, phone: message }));
          return;
        }
        console.error(message);
        return;
      }
      onSubmit?.(payload);
    } catch (err) {
      console.error(err);
    }
  };

  const isFormValid =
    formData.name &&
    formData.country &&
    !getPhoneValidationMessage(formData.phone, phoneCountry) &&
    MSG.POSTCODEREG.test(formData.postcode) &&
    formData.address.length >= 5;
  const textFieldSX = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      "& fieldset": {
        borderWidth: "1px",
        borderColor: "#E0E0E0",
      },
      minHeight: { xs: "42px", sm: "50px" },
      display: "flex",
      alignItems: "center",
      "& input": {
        fontSize: { xs: "12px", sm: "14px" },
        padding: { xs: "10px 14px", sm: "12px 16px" },
      },
      "& textarea": {
        fontSize: { xs: "12px", sm: "14px" },
        padding: "12px 16px",
      },
      "&.Mui-focused fieldset": {
        borderWidth: "1px",
        borderColor: themestyle.colors.activeGreen,
      },
      "&.Mui-error fieldset": {
        borderColor: "#d32f2f",
      },
    },
    "& .MuiFormHelperText-root": {
      fontSize: { xs: "9px", sm: "12px" },
      marginLeft: 0.5,
      marginTop: "4px",
    },
  };

  const Label = ({ text }: { text: string }) => (
    <Box sx={{  fontSize: { xs: "11px", sm: "12px", md: "13px" }, fontWeight: 500, mb: 0.5 }}>
      {text}
    </Box>
  );

  return (
    <Modal 
      open
      disableScrollLock={false}
      sx={{
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0,0,0,0.4)",
          p: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: themestyle.colors.white,
            borderRadius: "16px",
            width: isMobile ? "calc(100% - 10px)" : "700px",
            maxWidth: "95%",
            height: isMobile ? "calc(100dvh - 100px)" : "auto",
            maxHeight: "calc(100dvh - 40px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            fontFamily: themestyle.fontFamily.Poppins,
          }}
        >
          <form 
            onSubmit={handleSubmit} 
            autoComplete="off"
            style={{ 
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden"
            }}
          >
            <Box
              sx={{
                p: isMobile ? 3 : 4,
                pb: 1,
                background: themestyle.colors.white,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  fontSize: { xs: "16px", sm: "18px", md: "20px" }, 
                  fontWeight: 600,
                  color:themestyle.colors.activeGreen,
                }}
              >
                 {MESSAGES.ONBOARD_TITLE}
              </Box>
              <Box sx={{  fontSize: { xs: "10px", sm: "13px", md: "16px" }, color: themestyle.colors.black, mt: 1, fontWeight:500 }}>
                {MESSAGES.ONBOARD_SUBTITLE}
              </Box>
            </Box>
            <Box
              sx={{
                p: isMobile ? 3 : 4,
                pt: 1,
                overflowY: "auto", 
                flex: 1,
                pb: 1,
              }}
            >
              <Label text={FORM_LABELS.EMAIL} />
              <TextField
                fullWidth
                value={formData.email}
                placeholder={FORM_PLACEHOLDERS.EMAIL}
                InputProps={{ readOnly: true }}
                inputProps={{ autoComplete: "off" }}
                sx={textFieldSX}
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: 2,
                  mt: 2,
                }}
              >
                <Box>
                  <Label text={FORM_LABELS.FULL_NAME} />
                  <TextField
                    name="name"
                    value={formData.name}
                    placeholder={FORM_PLACEHOLDERS.FULL_NAME}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.name}
                    helperText={errors.name}
                    fullWidth
                    autoComplete="off"
                    sx={textFieldSX}
                  />
                </Box>
                <Box>
                  <Label text={FORM_LABELS.PHONE} />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      height: { xs: "42px", sm: "50px" },
                      border: "1px solid #E0E0E0",
                      borderColor: errors.phone ? "#d32f2f" : "#E0E0E0",
                      borderRadius: "6px",
                      backgroundColor: themestyle.colors.white,
                      overflow: "hidden",
                      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                      "&:focus-within": {
                        borderColor: themestyle.colors.activeGreen,
                      },
                    }}
                  >
                    <Box
                      onClick={handleCountryMenuOpen}
                      sx={{
                        width: "fit-content",
                        maxWidth: { xs: 110, sm: 122 },
                        px: 1.25,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        borderRight: "1px solid #E4E7EC",
                        cursor: "pointer",
                        flexShrink: 0,
                        height: "100%",
                      }}
                    >
                      {(() => {
                        const Flag = selectedPhoneCountry ? (Flags as any)[selectedPhoneCountry.code] : null;

                        return (
                          <>
                            <Box sx={{ width: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {Flag ? (
                                <Flag style={{ width: 20, height: 14, borderRadius: "2px" }} />
                              ) : (
                                <Typography sx={{ fontSize: 13 }}>
                                  {selectedPhoneCountry?.emoji || "🌐"}
                                </Typography>
                              )}
                            </Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1D2939" }}>
                              {selectedDialCode ? `+${selectedDialCode}` : "Code"}
                            </Typography>
                            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 17, color: "#98A2B3" }} />
                          </>
                        );
                      })()}
                    </Box>

                    <TextField
                      name="phone"
                      value={formData.phone}
                      placeholder={FORM_PLACEHOLDERS.PHONE_UK}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={false}
                      fullWidth
                      autoComplete="new-password"
                      variant="standard"
                      inputProps={{
                        inputMode: "numeric",
                        maxLength: PHONE_INPUT_MAX_DIGITS,
                      }}
                      InputProps={{ disableUnderline: true }}
                      sx={{
                        flex: "1 1 0",
                        minWidth: 0,
                        height: "100%",
                        "& .MuiInputBase-root": {
                          display: "flex",
                          alignItems: "center",
                          px: 2,
                          width: "100%",
                          height: "100%",
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "12px", sm: "14px" },
                          color: "#1D2939",
                          padding: 0,
                          height: "100%",
                          boxSizing: "border-box",
                        },
                      }}
                    />
                  </Box>
                  {errors.phone ? (
                    <Box
                      sx={{
                        fontSize: { xs: "9px", sm: "12px" },
                        color: "#d32f2f",
                        marginLeft: 0.5,
                        marginTop: "4px",
                        fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                      }}
                    >
                      {errors.phone}
                    </Box>
                  ) : null}

                  <Menu
                    anchorEl={countryMenuAnchor}
                    open={Boolean(countryMenuAnchor)}
                    onClose={handleCountryMenuClose}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        width: 320,
                        maxHeight: 380,
                        borderRadius: "16px",
                        border: "1px solid #D0D5DD",
                        boxShadow: "0 20px 40px rgba(16, 24, 40, 0.12)",
                        p: 0.5,
                      },
                    }}
                    MenuListProps={{ disablePadding: true }}
                  >
                    <Box sx={{ p: 1 }}>
                      <TextField
                        value={countrySearch}
                        onChange={(event) => setCountrySearch(event.target.value)}
                        placeholder="Search"
                        fullWidth
                        size="small"
                        autoFocus
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        InputProps={{
                          startAdornment: <SearchOutlinedIcon sx={{ color: "#98A2B3", mr: 1 }} />,
                        }}
                      />
                    </Box>
                    <Box sx={{ maxHeight: 300, overflowY: "auto", py: 0.5 }}>
                      {filteredCountryOptions.map((country) => {
                        const Flag = (Flags as any)[country.code];
                        const isActive = country.name === phoneCountry;

                        return (
                          <MenuItem
                            key={country.code}
                            onClick={() => {
                              handlePhoneCountryChange(country);
                              handleCountryMenuClose();
                            }}
                            sx={{
                              mx: 0.5,
                              mb: 0.25,
                              borderRadius: "10px",
                              display: "flex",
                              alignItems: "center",
                              gap: 1.25,
                              backgroundColor: isActive ? "rgba(30, 127, 61, 0.08)" : "transparent",
                            }}
                          >
                            <Box sx={{ width: 22, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {Flag ? (
                                <Flag style={{ width: 20, height: 14, borderRadius: "2px" }} />
                              ) : (
                                <span>{country.emoji}</span>
                              )}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: 14, color: "#1D2939" }}>
                                {country.name}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#667085" }}>
                              +{country.dialCode || "—"}
                            </Typography>
                          </MenuItem>
                        );
                      })}
                    </Box>
                  </Menu>
                </Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Label text={FORM_LABELS.ADDRESS} />
                <TextField
                  multiline
                  rows={2}
                  name="address"
                  value={formData.address}
                  placeholder={FORM_PLACEHOLDERS.ADDRESS}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.address}
                  helperText={errors.address}
                  fullWidth
                  autoComplete="off"
                  sx={{
                    ...textFieldSX,
                    "& .MuiOutlinedInput-root textarea": {
                      padding: "6px 6px",
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: 2,
                  mt: 2,
                }}
              >
                <Box>
                  <Label text={FORM_LABELS.COUNTRY} />
                  <Autocomplete
                    options={countryOptions}
                    getOptionLabel={(option) => option.name}
                    value={countryOptions.find((c) => c.name === formData.country) || null}
                    onChange={handleCountryChange}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props as any;
                      const Flag = (Flags as any)[option.code];
                      return (
                        <Box 
                          key={key}
                          component="li" 
                          {...optionProps} 
                          sx={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: 1.5, 
                            fontSize: { xs: "12px", sm: "14px" },
                            py: 1
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", width: 24, flexShrink: 0 }}>
                            {Flag ? <Flag style={{ width: "100%", height: "auto", borderRadius: "2px" }} /> : <span>{option.emoji}</span>}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            {option.name}
                          </Box>
                        </Box>
                      );
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        padding: "0px !important",
                        paddingLeft: "12px !important",
                        "& .MuiAutocomplete-input": {
                          padding: { xs: "10px 14px !important", sm: "12px 16px !important" },
                          paddingLeft: "8px !important",
                        },
                      },
                    }}
                    renderInput={(params) => {
                      const selectedCountry = countryOptions.find((c) => c.name === formData.country);
                      const Flag = selectedCountry ? (Flags as any)[selectedCountry.code] : null;
                      
                      return (
                        <TextField
                          {...params}
                          placeholder={FORM_PLACEHOLDERS.COUNTRY}
                          error={!!errors.country}
                          helperText={errors.country}
                          sx={textFieldSX}
                          autoComplete="new-password"
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: 'new-password',
                          }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: selectedCountry && Flag ? (
                              <Box sx={{ display: "flex", alignItems: "center", width: 20, mr: -1, ml: 0.5 }}>
                                <Flag style={{ width: "100%", height: "auto", borderRadius: "1px" }} />
                              </Box>
                            ) : params.InputProps.startAdornment
                          }}
                        />
                      );
                    }}
                    slotProps={{
                      popper: {
                        placement: "bottom-start",
                        modifiers: [
                          {
                            name: 'flip',
                            enabled: false,
                          },
                          {
                            name: 'preventOverflow',
                            options: {
                              boundary: 'viewport',
                            },
                          },
                        ],
                      },
                      paper: {
                        sx: {
                          mt: 1,
                          borderRadius: "10px",
                          boxShadow: "0px 10px 30px rgba(0,0,0,0.08)",
                          border: "1px solid #F0F0F0",
                          "& .MuiAutocomplete-listbox": {
                            maxHeight: "300px",
                            padding: "6px 0",
                            "& .MuiAutocomplete-option": {
                              padding: "10px 18px",
                              "&[aria-selected='true']": {
                                backgroundColor: "rgba(30, 127, 61, 0.08)",
                              },
                              "&.Mui-focused": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                              },
                            }
                          }
                        }
                      }
                    }}
                  />
                </Box>
                <Box>
                  <Label text={FORM_LABELS.POSTCODE} />
                  <TextField
                    name="postcode"
                    value={formData.postcode}
                    placeholder={FORM_PLACEHOLDERS.POSTCODE}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.postcode}
                    helperText={errors.postcode}
                    fullWidth
                    sx={textFieldSX}
                  />
                </Box>
              </Box>
              <Box sx={{ height: isMobile ? "24px" : "16px" }} />
            </Box>
            <Box
              sx={{
                p: isMobile ? 3 : 4,
                pt: 1,
                pb: 3,
                background:themestyle.colors.white,
                flexShrink: 0,
              }}
            >
              <Button
                type="submit"
                fullWidth
                disabled={!isFormValid || loading}
                sx={{
                  py: { xs: 1, sm: 1.2 },
                  borderRadius: "8px",
                  backgroundColor: themestyle.colors.activeGreen,
                  color: themestyle.colors.white,
                  fontWeight: 600,
                  fontSize: { xs: "12px", sm: "16px" },
                  textTransform: "none", 
                  "&:hover": {  backgroundColor: themestyle.colors.secondary,},
                  "&:disabled": {
                    backgroundColor:themestyle.colors.GRAY_DISABLED,
                    color: themestyle.colors.white,
                  },
                }}
              >
              {loading ? BUTTON_TEXTS.SAVING : BUTTON_TEXTS.SUBMIT}
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Modal>
  );
};
export default AdminOnboardModel;
